const APPOINTMENT_FILTERS_STORAGE_KEY = "healo-appointment-filters";
const APPOINTMENT_FILTERS_CHANGE_EVENT = "healo-appointment-filters-change";

// `null` for every facet means "no explicit selection" → the calendar shows everything
// that facet offers. `calendarIds` (standalone admin calendars) was added later, so
// blobs and presets that predate it come back as null = all standalone calendars visible;
// an explicit `[]` means none.
const DEFAULT_APPOINTMENT_FILTERS = Object.freeze({
  tenantIds: null,
  doctorIds: null,
  calendarIds: null,
});

const APPOINTMENT_FILTER_KEYS = ["tenantIds", "doctorIds", "calendarIds"];

let inMemoryAppointmentFilters = { ...DEFAULT_APPOINTMENT_FILTERS };
let hasLoadedAppointmentFilters = false;

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeIdArray(value) {
  if (!Array.isArray(value)) {
    return null;
  }
  const uniqueIds = [];
  const seen = new Set();
  value.forEach((item) => {
    const id = String(item || "").trim();
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    uniqueIds.push(id);
  });
  return uniqueIds;
}

export function normalizeAppointmentFilters(value) {
  const source = value && typeof value === "object" ? value : {};
  const normalized = {};
  for (const key of APPOINTMENT_FILTER_KEYS) {
    normalized[key] = normalizeIdArray(source[key]);
  }
  return normalized;
}

function areStringArraysEqual(left, right) {
  if (left === right) {
    return true;
  }
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}

export function areAppointmentFiltersEqual(left, right) {
  for (const key of APPOINTMENT_FILTER_KEYS) {
    const leftValue = left?.[key];
    const rightValue = right?.[key];
    if (leftValue === rightValue) {
      continue;
    }
    if (
      !Array.isArray(leftValue) ||
      !Array.isArray(rightValue) ||
      !areStringArraysEqual(leftValue, rightValue)
    ) {
      return false;
    }
  }
  return true;
}

function emitAppointmentFiltersChange() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(APPOINTMENT_FILTERS_CHANGE_EVENT));
}

function readFiltersFromStorage() {
  if (!canUseBrowserStorage()) {
    return { ...DEFAULT_APPOINTMENT_FILTERS };
  }
  const rawFilters = window.localStorage.getItem(APPOINTMENT_FILTERS_STORAGE_KEY);
  if (!rawFilters) {
    return { ...DEFAULT_APPOINTMENT_FILTERS };
  }
  try {
    const parsed = JSON.parse(rawFilters);
    return normalizeAppointmentFilters(parsed);
  } catch {
    return { ...DEFAULT_APPOINTMENT_FILTERS };
  }
}

export function getAppointmentFilters() {
  if (hasLoadedAppointmentFilters) {
    return inMemoryAppointmentFilters;
  }
  inMemoryAppointmentFilters = readFiltersFromStorage();
  hasLoadedAppointmentFilters = true;
  return inMemoryAppointmentFilters;
}

export function setAppointmentFilters(nextFilters) {
  const previous = getAppointmentFilters();
  const normalizedNext = normalizeAppointmentFilters({
    ...previous,
    ...(nextFilters && typeof nextFilters === "object" ? nextFilters : {}),
  });

  if (areAppointmentFiltersEqual(previous, normalizedNext)) {
    return previous;
  }

  inMemoryAppointmentFilters = normalizedNext;
  hasLoadedAppointmentFilters = true;

  if (canUseBrowserStorage()) {
    window.localStorage.setItem(APPOINTMENT_FILTERS_STORAGE_KEY, JSON.stringify(inMemoryAppointmentFilters));
  }

  emitAppointmentFiltersChange();
  return inMemoryAppointmentFilters;
}

export function subscribeAppointmentFilters(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getAppointmentFilters());
  window.addEventListener(APPOINTMENT_FILTERS_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener(APPOINTMENT_FILTERS_CHANGE_EVENT, handler);
  };
}

export function clearAppointmentFilters() {
  inMemoryAppointmentFilters = { ...DEFAULT_APPOINTMENT_FILTERS };
  hasLoadedAppointmentFilters = true;
  if (canUseBrowserStorage()) {
    window.localStorage.removeItem(APPOINTMENT_FILTERS_STORAGE_KEY);
  }
  emitAppointmentFiltersChange();
}
